import { useCallback, useEffect, useRef } from 'react';
import { OPEN_SETUP_SECTION_EVENT } from '../renderer/FirstReviewGuide';

/**
 * A job opened on its setup from Paige's jobs home (the card menu's Job setup, `?setup=1` on the
 * review route; Dustin, 2026-10-01). Three steps, each through a path the App already has:
 *
 * 1. Before the App mounts, the host writes a one-shot key (`App.tsx` `setupRequestKey`), so the App
 *    mounts in Job setup rather than loading the documents first.
 * 2. Job setup's mount resets its section to Document types (CapturePoint's own effect on the job),
 *    so on its first opening the job goes to Overview through the job door's path
 *    (`OPEN_SETUP_SECTION_EVENT`, `openJobSetupAt`). It is sent inside the App's own `opened` call,
 *    so its state change lands in the same render and Document types is never painted first.
 *    A job still in setup keeps CapturePoint's own first section, as Continue setup opens it.
 * 3. Once the App has opened, the host is told (`onSetupShown`) and drops its request, so a reload
 *    opens the documents.
 */
export const SETUP_REQUEST_KEY = 'capturepoint:setup-request:v1';

/**
 * Which setup was asked for: Overview (`true`, the card menu's Job setup) or Export (`'export'`, the
 * way back from a storage sign-in, `?setup=export&destination=`; Dustin, 2026-10-01).
 */
export type SetupRequest = boolean | 'export';

/**
 * The destination whose sheet the Export section opens once (SendResultsCard), written beside an
 * Export request. Taken, not withdrawn with the request: the card may mount after the host dropped it.
 */
export const DESTINATION_REQUEST_KEY = 'paige:destination-request:v1';

/** Writes or withdraws the one-shot key the App reads on mount (step 1), and the destination to open. */
export function pinSetupRequest(openSetup: SetupRequest, destination?: string): void {
  if (openSetup) window.sessionStorage.setItem(SETUP_REQUEST_KEY, '1');
  else window.sessionStorage.removeItem(SETUP_REQUEST_KEY);
  if (openSetup === 'export' && destination)
    window.sessionStorage.setItem(DESTINATION_REQUEST_KEY, destination);
}

/** The destination the Export section was asked to open, once; null when none was. */
export function takeDestinationRequest(): string | null {
  try {
    const id = window.sessionStorage.getItem(DESTINATION_REQUEST_KEY);
    window.sessionStorage.removeItem(DESTINATION_REQUEST_KEY);
    return id;
  } catch {
    return null;
  }
}

/** The bridge's `opened`, moving a requested setup to its section the first time (step 2). */
export function useSetupOpening(
  openSetup: SetupRequest,
  mode: 'capture' | 'setup',
  markOpened: () => void,
): () => void {
  // Export is asked for by a person who was there, so a job still in setup goes there too.
  const pending = useRef(openSetup === 'export' || (openSetup && mode !== 'setup'));
  const section = openSetup === 'export' ? 'export' : 'overview';
  return useCallback(() => {
    if (pending.current) {
      pending.current = false;
      window.dispatchEvent(new CustomEvent(OPEN_SETUP_SECTION_EVENT, { detail: section }));
    }
    markOpened();
  }, [markOpened, section]);
}

/** Tells the host its request is on screen once the App has opened (step 3). */
export function useSetupShown(
  opened: boolean,
  openSetup: SetupRequest | undefined,
  onSetupShown: (() => void) | undefined,
): void {
  useEffect(() => {
    if (opened && openSetup) onSetupShown?.();
  }, [opened, openSetup, onSetupShown]);
}
